const headerComponent = document.querySelector('.header');

if (headerComponent) {
  const navbarNav = headerComponent.querySelector('.navbar-nav');
  const burgerButton = headerComponent.querySelector('.burger-btn');
  const headerCloseWrapper = headerComponent.parentElement?.querySelector(
    '.header-close-wrapper'
  );
  const mobileMenuStartPoint = Number.parseInt(
    getComputedStyle(document.documentElement).getPropertyValue(
      '--mobile-menu-start-point'
    ),
    10
  );
  const mobileMenuBreakpoint = Number.isNaN(mobileMenuStartPoint)
    ? 1200
    : mobileMenuStartPoint;
  const mobileOverlayBreakpoint = 640;
  const desktopDropdownBreakpoint = 1200;
  const headerDropdownActiveClass = 'is-dropdown-active';
  const dropdownVisibleClass = 'is-visible';
  const dropdownItems = headerComponent.querySelectorAll('.menu-item.dropdown');
  const dropdownLinks = headerComponent.querySelectorAll(
    '.menu-item.dropdown > .menu-link'
  );
  let desktopDropdownCloseTimeout = null;
  let lastFocusedElement = null;
  let scrollFrame = null;

  const isMobileMenuMode = () => window.innerWidth <= mobileMenuBreakpoint;
  const isDesktopDropdownMode = () => window.innerWidth > desktopDropdownBreakpoint;
  const isMenuOpen = () => headerComponent.classList.contains('is-open-menu');

  const setPageScrollLock = (isLocked) => {
    const scrollLockApi = window.scrollLock;

    if (isLocked) {
      const scrollableMenu = navbarNav?.querySelector('.menu');

      if (scrollableMenu) {
        scrollLockApi?.disablePageScroll(scrollableMenu);
        scrollLockApi?.addScrollableSelector('.simplebar-content-wrapper');
      }

      window.lenis?.stop();
      return;
    }

    scrollLockApi?.enablePageScroll();
    window.lenis?.start();
  };

  const getMenuFocusableElements = () => {
    if (!navbarNav || !burgerButton) {
      return [];
    }

    return [
      burgerButton,
      ...navbarNav.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((element) => element instanceof HTMLElement && !element.hasAttribute('disabled'));
  };

  const resetMobileDropdowns = () => {
    dropdownItems.forEach((item) => {
      const dropdownMenu = item.querySelector('.dropdown-menu');

      item.classList.remove('active');
      dropdownMenu?.style.removeProperty('max-height');
      dropdownMenu?.style.removeProperty('opacity');
    });
  };

  const syncMenuState = () => {
    if (!navbarNav || !burgerButton) {
      return;
    }

    if (!isMobileMenuMode()) {
      headerComponent.classList.remove('is-open-menu');
      navbarNav.classList.remove('active');
      burgerButton.classList.remove('active');
      headerCloseWrapper?.classList.remove('active');
      burgerButton.setAttribute('aria-expanded', 'false');
      burgerButton.setAttribute('aria-label', 'Open navigation menu');
      navbarNav.setAttribute('aria-hidden', 'false');
      navbarNav.inert = false;
      resetMobileDropdowns();
      setPageScrollLock(false);
      return;
    }

    const menuIsOpen = isMenuOpen();

    navbarNav.classList.toggle('active', menuIsOpen);
    burgerButton.classList.toggle('active', menuIsOpen);
    headerCloseWrapper?.classList.toggle('active', menuIsOpen);
    burgerButton.setAttribute('aria-expanded', String(menuIsOpen));
    burgerButton.setAttribute(
      'aria-label',
      menuIsOpen ? 'Close navigation menu' : 'Open navigation menu'
    );
    navbarNav.setAttribute('aria-hidden', String(!menuIsOpen));
    navbarNav.inert = !menuIsOpen;
  };

  const openMenu = () => {
    if (!isMobileMenuMode() || !navbarNav || !burgerButton || isMenuOpen()) {
      return;
    }

    lastFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : burgerButton;
    headerComponent.classList.add('is-open-menu');
    headerComponent.style.top = '0';
    syncMenuState();
    setPageScrollLock(true);

    window.requestAnimationFrame(() => {
      navbarNav.querySelector('a[href], button:not([disabled])')?.focus();
    });
  };

  const closeMenu = ({ restoreFocus = false } = {}) => {
    if (!navbarNav || !burgerButton || !isMenuOpen()) {
      return;
    }

    headerComponent.classList.remove('is-open-menu');
    resetMobileDropdowns();
    syncMenuState();
    setPageScrollLock(false);

    if (restoreFocus) {
      (lastFocusedElement?.isConnected ? lastFocusedElement : burgerButton).focus();
    }
  };

  const cancelDesktopDropdownClose = () => {
    if (!desktopDropdownCloseTimeout) {
      return;
    }

    window.clearTimeout(desktopDropdownCloseTimeout);
    desktopDropdownCloseTimeout = null;
  };

  const syncDesktopDropdownWidth = () => {
    dropdownItems.forEach((item) => {
      const dropdownMenu = item.querySelector('.dropdown-menu');

      if (!dropdownMenu) {
        return;
      }

      if (!isDesktopDropdownMode()) {
        dropdownMenu.style.removeProperty('--dropdown-menu-width');
        return;
      }

      const maxLinkWidth = Array.from(
        dropdownMenu.querySelectorAll('.menu-link')
      ).reduce((width, link) => Math.max(width, Math.ceil(link.scrollWidth)), 0);

      dropdownMenu.style.setProperty(
        '--dropdown-menu-width',
        `${Math.max(maxLinkWidth ? maxLinkWidth + 56 : 200, 200)}px`
      );
    });
  };

  const showDesktopDropdown = (item) => {
    const dropdownMenu = item?.querySelector('.dropdown-menu');

    if (!dropdownMenu) {
      return;
    }

    dropdownMenu.style.display = 'block';
    window.requestAnimationFrame(() => {
      dropdownMenu.classList.add(dropdownVisibleClass);
    });
  };

  const hideDesktopDropdown = (item) => {
    const dropdownMenu = item?.querySelector('.dropdown-menu');

    if (!dropdownMenu) {
      return;
    }

    dropdownMenu.classList.remove(dropdownVisibleClass);
    window.setTimeout(() => {
      if (!item.classList.contains('active')) {
        dropdownMenu.style.removeProperty('display');
      }
    }, 240);
  };

  const setActiveDesktopDropdown = (currentDropdown) => {
    dropdownItems.forEach((item) => {
      const isCurrent = item === currentDropdown;

      item.classList.toggle('active', isCurrent);

      if (isCurrent) {
        showDesktopDropdown(item);
      } else {
        hideDesktopDropdown(item);
      }
    });

    headerComponent.classList.toggle(
      headerDropdownActiveClass,
      Boolean(currentDropdown)
    );
  };

  const clearDesktopDropdownState = () => {
    cancelDesktopDropdownClose();
    dropdownItems.forEach((item) => {
      item.classList.remove('active');
      hideDesktopDropdown(item);
    });
    headerComponent.classList.remove(headerDropdownActiveClass);
  };

  const scheduleDesktopDropdownClose = () => {
    if (!isDesktopDropdownMode()) {
      return;
    }

    cancelDesktopDropdownClose();
    desktopDropdownCloseTimeout = window.setTimeout(clearDesktopDropdownState, 120);
  };

  dropdownLinks.forEach((link) => {
    link.addEventListener('mouseenter', () => {
      if (!isDesktopDropdownMode()) {
        return;
      }

      cancelDesktopDropdownClose();
      setActiveDesktopDropdown(link.closest('.menu-item.dropdown'));
    });
  });

  dropdownItems.forEach((item) => {
    item.addEventListener('mouseenter', () => {
      if (!isDesktopDropdownMode()) {
        return;
      }

      cancelDesktopDropdownClose();
      setActiveDesktopDropdown(item);
    });

    item.addEventListener('mouseleave', scheduleDesktopDropdownClose);
  });

  headerComponent.querySelectorAll('.dropdown-toggle').forEach((toggle) => {
    toggle.addEventListener('click', (event) => {
      if (!isMobileMenuMode()) {
        return;
      }

      const menuItem = toggle.closest('.menu-item.dropdown');
      const dropdownMenu = toggle.nextElementSibling;

      if (!menuItem || !(dropdownMenu instanceof HTMLElement)) {
        return;
      }

      event.preventDefault();
      const isAlreadyActive = menuItem.classList.contains('active');

      resetMobileDropdowns();

      if (isAlreadyActive) {
        return;
      }

      menuItem.classList.add('active');
      dropdownMenu.style.maxHeight = `${dropdownMenu.scrollHeight}px`;
      dropdownMenu.style.opacity = '1';
    });
  });

  burgerButton?.addEventListener('click', () => {
    if (isMenuOpen()) {
      closeMenu({ restoreFocus: true });
      return;
    }

    openMenu();
  });

  headerCloseWrapper?.addEventListener('click', () => {
    closeMenu({ restoreFocus: true });
  });

  navbarNav?.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('a[href]') : null;

    if (target && isMenuOpen()) {
      closeMenu();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!isMenuOpen()) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu({ restoreFocus: true });
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getMenuFocusableElements();

    if (!focusableElements.length) {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements.at(-1);

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  });

  const updateMenu = () => {
    if (window.innerWidth > mobileOverlayBreakpoint && isMenuOpen()) {
      closeMenu();
    }

    if (!isDesktopDropdownMode()) {
      clearDesktopDropdownState();
    }

    syncMenuState();
    syncDesktopDropdownWidth();
  };

  const updateHeaderStickyState = () => {
    scrollFrame = null;

    if (isMenuOpen()) {
      headerComponent.style.top = '0';
      return;
    }

    const currentScrollPos = window.scrollY;
    const previousScrollPos = Number(headerComponent.dataset.previousScrollPosition || 0);
    const headerHeight = headerComponent.scrollHeight;

    if (currentScrollPos > 100 && previousScrollPos <= currentScrollPos) {
      headerComponent.style.top = `-${headerHeight + 3}px`;
    } else {
      headerComponent.style.top = '0';
    }

    headerComponent.classList.toggle('scrolled', currentScrollPos > 0);
    headerComponent.dataset.previousScrollPosition = String(currentScrollPos);
  };

  const handleHeaderScroll = () => {
    if (scrollFrame !== null) {
      return;
    }

    scrollFrame = window.requestAnimationFrame(updateHeaderStickyState);
  };

  window.addEventListener('resize', updateMenu);
  window.addEventListener('orientationchange', updateMenu);
  window.addEventListener('scroll', handleHeaderScroll, { passive: true });
  window.addEventListener('load', syncDesktopDropdownWidth, { once: true });

  updateMenu();
  updateHeaderStickyState();
}
